/**
 * Small controls for exec tiles (quick-261104-ia3): the week / month / quarter
 * toggle and the vertical "blood drive" gauge.
 */
import { money } from '../../lib/exec/fmt.js';

import type { Grain } from '../../lib/exec/periods.js';

const GRAIN_LABEL: Record<Grain, string> = { week: 'Week', month: 'Month', quarter: 'Qtr' };

export function PeriodToggle({
  value,
  onChange,
  options = ['week', 'month', 'quarter'],
}: {
  value: Grain;
  onChange: (g: Grain) => void;
  options?: Grain[];
}): React.JSX.Element {
  return (
    <div role="radiogroup" aria-label="Period" className="inline-flex rounded-md border border-ink-200 bg-ink-50 p-0.5">
      {options.map((g) => (
        <button
          key={g}
          type="button"
          role="radio"
          aria-checked={value === g}
          onClick={() => {
            onChange(g);
          }}
          className={`rounded px-2 py-0.5 text-[0.68rem] font-medium transition-colors ${
            value === g ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
          }`}
        >
          {GRAIN_LABEL[g]}
        </button>
      ))}
    </div>
  );
}

/**
 * BloodDrive — a vertical thermometer that fills toward a target (John: "mimic
 * blood drives where possible. if there is a target, have it vertical, how are
 * we filling up"). Optional inner `mature` fill shows the firmer part of the
 * total. Reaching the target turns the cap green with a ✓ (icon + text, never
 * colour alone).
 */
export function BloodDrive({
  label,
  value,
  target,
  mature,
  targetNote,
  height = 150,
  valueFormat = money,
}: {
  label: string;
  value: number;
  target: number | null;
  mature?: number | null;
  targetNote?: string;
  height?: number;
  valueFormat?: (n: number) => string;
}): React.JSX.Element {
  const hasTarget = target != null && target > 0;
  const scaleMax = (hasTarget ? Math.max(target, value) : Math.max(value, 1)) * 1.12;
  const fillPct = Math.min(100, (value / scaleMax) * 100);
  const maturePct = mature != null ? Math.min(100, (mature / scaleMax) * 100) : null;
  const targetPct = hasTarget ? (target / scaleMax) * 100 : null;
  const pctOfTarget = hasTarget ? value / target : null;
  const reached = pctOfTarget != null && pctOfTarget >= 1;

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
      <div className="text-center leading-tight">
        <div className="text-sm font-semibold text-ink-900 tabular-nums">{valueFormat(value)}</div>
        <div className="text-[0.66rem] text-ink-500">
          {hasTarget ? (
            <>
              {reached ? <span className="font-semibold text-good-text">✓ </span> : null}
              {Math.round((pctOfTarget ?? 0) * 100)}% of {valueFormat(target)}
            </>
          ) : (
            'no target set'
          )}
        </div>
      </div>
      <div
        className="relative w-9 overflow-hidden rounded-full border border-ink-200 bg-ink-50"
        style={{ height }}
        role="img"
        aria-label={`${label}: ${valueFormat(value)}${hasTarget ? ` of ${valueFormat(target)} target` : ''}`}
      >
        <div className="absolute inset-x-0 bottom-0 bg-[#86b6ef]" style={{ height: `${String(fillPct)}%` }} />
        {maturePct != null ? (
          <div className="absolute inset-x-0 bottom-0 bg-[#1c5cab]" style={{ height: `${String(maturePct)}%` }} />
        ) : null}
        {targetPct != null ? (
          <div
            className={`absolute inset-x-0 border-t-2 ${reached ? 'border-good' : 'border-ink-900'}`}
            style={{ bottom: `${String(targetPct)}%` }}
          />
        ) : null}
      </div>
      <div className="text-center text-[0.7rem] font-medium text-ink-700">{label}</div>
      {targetNote ? <div className="text-center text-[0.62rem] text-ink-400">{targetNote}</div> : null}
    </div>
  );
}
